import { memo } from 'react'
import { ChevronRight } from 'lucide-react'
import type { Program } from '../../types/index.js'
import { formatDuration, PROGRAM_TYPE_LABELS } from '../../lib/utils.js'
import { programReadiness, effectiveGenreLabel } from './readiness.js'
import { Checkbox } from '../ui/checkbox.js'
import './ProgramsView.css'

/**
 * Tableau du catalogue : une ligne par fiche, en colonnes lisibles.
 *
 * L'ancienne liste étroite forçait à tronquer le titre et n'affichait ni la
 * série, ni la durée, ni l'état éditorial — l'opérateur devait ouvrir chaque
 * fiche pour savoir laquelle il cherchait. Les colonnes rendent ces quatre
 * informations comparables d'un coup d'œil.
 *
 * La sélection reprend exactement les gestes des autres écrans : case
 * d'en-tête, Maj+clic pour une plage, compteur et actions groupées au-dessus.
 */

export type CatalogSortKey = 'title' | 'durationSec' | 'createdAt'

interface ProgramTableProps {
  programs:     Program[]
  selectedId:   string | null
  checkedIds:   Set<string>
  sortBy:       CatalogSortKey
  sortDir:      'asc' | 'desc'
  onSort:       (key: CatalogSortKey) => void
  onSelect:     (program: Program) => void
  onOpen:       (program: Program) => void
  onCheck:      (program: Program, checked: boolean, shiftKey: boolean) => void
  onCheckAll:   () => void
  allChecked:   boolean
  someChecked:  boolean
  loading:      boolean
  /**
   * Bande « Demain et plus loin ». Absente : pas de bande (tableau simple).
   * Présente, elle sépare la liste principale des fiches prévues après la
   * journée d'antenne en cours, qui ne s'affichent qu'une fois dépliée.
   */
  upcoming?:    UpcomingGroup
}

export interface UpcomingGroup {
  /** Lignes dans leur ordre d'affichage (les plus lointaines en haut). */
  programs:    Program[]
  total:       number
  expanded:    boolean
  loading:     boolean
  hasMore:     boolean
  loadingMore: boolean
  onToggle:    () => void
  onLoadMore:  () => void
}

export const ProgramTable = memo(function ProgramTable({
  programs, selectedId, checkedIds, sortBy, sortDir, onSort,
  onSelect, onOpen, onCheck, onCheckAll, allChecked, someChecked, loading, upcoming,
}: ProgramTableProps) {
  const renderRow = (program: Program) => (
    <ProgramTableRow
      key={program.id}
      program={program}
      active={program.id === selectedId}
      checked={checkedIds.has(program.id)}
      onSelect={onSelect}
      onOpen={onOpen}
      onCheck={onCheck}
    />
  )
  // Une fiche redatée entre deux chargements peut transiter par les deux
  // listes : elle ne s'affiche qu'une fois, du côté de sa date actuelle.
  const upcomingIds = upcoming?.expanded ? new Set(upcoming.programs.map(program => program.id)) : null
  const currentRows = upcomingIds ? programs.filter(program => !upcomingIds.has(program.id)) : programs

  return (
    <div className="catalog-table" role="table" aria-label="Programmes">
      <div className="catalog-table__head" role="row">
        <span className="catalog-table__cell catalog-table__cell--check" role="columnheader">
          <Checkbox
            checked={allChecked ? true : someChecked ? 'indeterminate' : false}
            disabled={loading || (programs.length === 0 && !(upcoming?.expanded && upcoming.programs.length > 0))}
            onClick={onCheckAll}
            aria-label="Sélectionner tous les programmes affichés"
          />
        </span>
        <span className="catalog-table__cell catalog-table__cell--genre" role="columnheader">Format</span>
        <SortableHeader
          className="catalog-table__cell--title"
          label="Programme"
          sortKey="title"
          activeKey={sortBy}
          dir={sortDir}
          onSort={onSort}
        />
        <span className="catalog-table__cell catalog-table__cell--series" role="columnheader">Série</span>
        <SortableHeader
          className="catalog-table__cell--duration"
          label="Durée"
          sortKey="durationSec"
          activeKey={sortBy}
          dir={sortDir}
          onSort={onSort}
        />
        <span className="catalog-table__cell catalog-table__cell--state" role="columnheader">État</span>
      </div>

      <div className="catalog-table__body">
        {upcoming && upcoming.expanded && (
          <div role="rowgroup" aria-label="Programmes de demain et plus loin" id="catalog-upcoming-rows">
            {/* Les plus lointaines en haut : leur suite se charge au-dessus,
                jamais entre demain et la bande. */}
            {upcoming.hasMore && (
              <div className="catalog-table__upcoming-more" role="row">
                <button type="button" onClick={upcoming.onLoadMore} disabled={upcoming.loadingMore}>
                  {upcoming.loadingMore
                    ? 'Chargement…'
                    : `Afficher plus loin (${upcoming.total - upcoming.programs.length} restant${upcoming.total - upcoming.programs.length > 1 ? 's' : ''})`}
                </button>
              </div>
            )}
            {upcoming.programs.map(renderRow)}
          </div>
        )}
        {upcoming && (upcoming.total > 0 || upcoming.expanded) && (
          <UpcomingBand upcoming={upcoming} />
        )}
        {currentRows.map(renderRow)}
      </div>
    </div>
  )
})

/**
 * Séparateur entre demain et aujourd'hui, et seul accès aux fiches à venir.
 * Il reste dans le flux de la liste — pas collé en haut — : déplié, il marque
 * la frontière entre les deux groupes et défile avec eux.
 */
const UpcomingBand = memo(function UpcomingBand({ upcoming }: { upcoming: UpcomingGroup }) {
  const { total, expanded, loading, onToggle } = upcoming
  const count = loading && total === 0 ? '…' : String(total)
  return (
    <div className={`catalog-table__upcoming-band${expanded ? ' is-expanded' : ''}`} role="row">
      <button
        type="button"
        className="catalog-table__upcoming-toggle"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={expanded ? 'catalog-upcoming-rows' : undefined}
      >
        <ChevronRight size={14} aria-hidden="true" className="catalog-table__upcoming-chevron" />
        <span className="catalog-table__upcoming-label">Demain et plus loin</span>
        <span className="catalog-table__upcoming-count">{count}</span>
        <span className="catalog-table__upcoming-hint">
          {expanded ? 'Masquer' : `Afficher ${total > 1 ? 'les programmes à venir' : 'le programme à venir'}`}
        </span>
      </button>
      {/* Repère de lecture sous la bande dépliée : ce qui suit est
          aujourd'hui et avant. */}
      {expanded && <span className="catalog-table__upcoming-today" aria-hidden="true">Aujourd’hui et avant</span>}
    </div>
  )
})

const SortableHeader = memo(function SortableHeader({ label, sortKey, activeKey, dir, onSort, className }: {
  label:     string
  sortKey:   CatalogSortKey
  activeKey: CatalogSortKey
  dir:       'asc' | 'desc'
  onSort:    (key: CatalogSortKey) => void
  className: string
}) {
  const active = activeKey === sortKey
  return (
    <span className={`catalog-table__cell ${className}`} role="columnheader" aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="catalog-table__sort" onClick={() => onSort(sortKey)}>
        {label}
        {active && <span aria-hidden="true">{dir === 'asc' ? ' ↑' : ' ↓'}</span>}
      </button>
    </span>
  )
})

const ProgramTableRow = memo(function ProgramTableRow({ program, active, checked, onSelect, onOpen, onCheck }: {
  program:  Program
  active:   boolean
  checked:  boolean
  onSelect: (program: Program) => void
  onOpen:   (program: Program) => void
  onCheck:  (program: Program, checked: boolean, shiftKey: boolean) => void
}) {
  const readiness = programReadiness(program)
  const genre = effectiveGenreLabel(program)
  const format = program.programCategory?.label ?? PROGRAM_TYPE_LABELS[program.type] ?? program.type

  // Le média se lit dans la règle partagée plutôt que recalculé ici : une
  // seconde définition finirait par diverger de celle qui décide du badge.
  const hasMedia = readiness.checks.find(check => check.id === 'media')?.satisfied ?? false

  const duration = program.totalDurationSec ?? program.durationSec
  const subtitle = program.epgSubtitle
    || (program.titleShort && program.titleShort !== program.title && program.titleShort !== program.series?.title
      ? program.titleShort
      : program.programCode)

  return (
    <div
      className={`catalog-table__row${active ? ' is-active' : ''}${checked ? ' is-selected' : ''}${program.active ? '' : ' is-archived'}`}
      role="row"
    >
      <span className="catalog-table__cell catalog-table__cell--check" role="cell">
        <Checkbox
          checked={checked}
          onClick={event => {
            // Sans ça, cocher une ligne ouvrirait aussi sa fiche.
            event.stopPropagation()
            onCheck(program, !checked, event.shiftKey)
          }}
          aria-label={`Sélectionner ${program.title}`}
        />
      </span>

      <button
        type="button"
        className="catalog-table__open"
        aria-current={active ? 'true' : undefined}
        onClick={() => onSelect(program)}
        onDoubleClick={() => onOpen(program)}
        title="Double-cliquer pour ouvrir la fiche"
      >
        <span className="catalog-table__cell catalog-table__cell--genre" role="cell">
          <span
            className="catalog-table__format-badge"
            style={program.programCategory?.color ? { '--catalog-accent': program.programCategory.color } as React.CSSProperties : undefined}
            title={`${format}${genre ? ` · ${genre}` : ''}`}
          >
            {format}
          </span>
        </span>

        <span className="catalog-table__cell catalog-table__cell--title" role="cell">
          <span className="catalog-table__title">
            <span className="catalog-table__title-text">{program.title}</span>
            {/* Visible en périmètre « Actifs et archivés », où la seule teinte
                de la ligne ne dirait pas laquelle est retirée de l'antenne. */}
            {!program.active && <span className="catalog-table__archived-badge">archivé</span>}
          </span>
          {/* Le titre EPG ne s'affiche que s'il apporte une information : il
              vaut très souvent le nom de la série, déjà dans sa colonne, et le
              répéter faisait lire deux fois la même chose côte à côte. */}
          {subtitle && <span className="catalog-table__subtitle">{subtitle}</span>}
        </span>

        <span className="catalog-table__cell catalog-table__cell--series" role="cell">
          {program.series?.title ?? '—'}
        </span>

        <span className="catalog-table__cell catalog-table__cell--duration" role="cell">
          {formatDuration(duration)}
        </span>

        <span className="catalog-table__cell catalog-table__cell--state" role="cell">
          {/* Un direct se signale comme tel : il n'a pas de fichier par nature,
              et l'afficher « prêt » comme un programme enregistré masquerait la
              seule chose qui le distingue en régie. */}
          {program.isLive && <span className="catalog-badge catalog-badge--live">DIRECT</span>}
          {!program.isLive && !hasMedia && <span className="catalog-badge catalog-badge--danger">SANS MÉDIA</span>}
          {!program.isLive && hasMedia && (
            readiness.ready
              ? <span className="catalog-badge catalog-badge--ok">PRÊT</span>
              : <span className="catalog-badge catalog-badge--warn">À COMPLÉTER</span>
          )}
        </span>
      </button>
    </div>
  )
})
